import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getDescendantProjects, transitionProject } from '../../services/api/projects';
import { Spinner } from '../common/Spinner';
import { StatusBadge } from '../workflow/StatusBadge';
import { Button } from '../common/Button';
import ProjectEditModal from './ProjectEditModal';

interface Props {
  projectId: number;
  childCount: number;
  showAddButton?: boolean;
}

export default function SubProjectsList({ projectId, childCount, showAddButton }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);

  const { data: descendants, isLoading, error } = useQuery({
    queryKey: ['descendantProjects', projectId],
    queryFn: () => getDescendantProjects(projectId),
    enabled: childCount > 0,
  });

  const transitionMutation = useMutation({
    mutationFn: ({ id, action }: { id: number; action: string }) => transitionProject(id, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['descendantProjects', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  if (childCount === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Sub-projects ({childCount})</h2>
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !descendants) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Sub-projects ({childCount})</h2>
        <p className="text-sm text-red-600">Failed to load sub-projects</p>
      </div>
    );
  }

  const filtered = showArchived
    ? descendants
    : descendants.filter((p) => p.state !== 'archived');

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-medium text-gray-900">Sub-projects ({filtered.length})</h2>
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Show archived
          </label>
        </div>
        {showAddButton && (
          <Button size="sm" variant="secondary" onClick={() => navigate(`/projects/new?parentId=${projectId}`)}>
            New Subproject
          </Button>
        )}
      </div>
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Subprojects table">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Labels
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                State
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((project) => (
              <tr key={project.id} className={`hover:bg-gray-50 ${project.depth > 0 ? 'bg-gray-50/50' : ''}`}>
                <td className="px-4 py-3 text-sm">
                  <div style={{ paddingLeft: `${project.depth * 20}px` }} className="flex items-center">
                    {project.depth > 0 && (
                      <span className="text-gray-300 mr-2">└</span>
                    )}
                    <Link
                      to={`/projects/${project.id}`}
                      className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                    >
                      {project.name}
                    </Link>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {project.labels && project.labels.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {project.labels.map((label) => (
                        <span
                          key={label.id}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                        >
                          {label.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-300">&mdash;</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  <StatusBadge state={project.state} />
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setEditingProjectId(project.id)}
                      className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                    >
                      Edit
                    </button>
                    {project.state === 'in_progress' && (
                      <>
                        <button
                          onClick={() => transitionMutation.mutate({ id: project.id, action: 'pause' })}
                          disabled={transitionMutation.isPending}
                          className="px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Pause
                        </button>
                        <button
                          onClick={() => transitionMutation.mutate({ id: project.id, action: 'complete' })}
                          disabled={transitionMutation.isPending}
                          className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Complete
                        </button>
                      </>
                    )}
                    {(project.state === 'in_progress' || project.state === 'paused' || project.state === 'completed') && (
                      <button
                        onClick={() => transitionMutation.mutate({ id: project.id, action: 'cancel' })}
                        disabled={transitionMutation.isPending}
                        className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Archive
                      </button>
                    )}
                    {project.state === 'paused' && (
                      <button
                        onClick={() => transitionMutation.mutate({ id: project.id, action: 'resume' })}
                        disabled={transitionMutation.isPending}
                        className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Resume
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editingProjectId && (
        <ProjectEditModal projectId={editingProjectId} onClose={() => setEditingProjectId(null)} />
      )}
    </div>
  );
}
